import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMe, logout as apiLogout, type AuthUser } from '@/lib/auth'
import { isLoggedIn } from '@/lib/auth'
import { useSessionKeepAlive } from '@/hooks/useSessionKeepAlive'

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  logout: () => Promise<void>
  setUser: Dispatch<SetStateAction<AuthUser | null>>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Proactively refresh token on user activity (prevents random logouts)
  useSessionKeepAlive()

  useEffect(() => {
    if (!isLoggedIn()) {
      setLoading(false)
      return
    }
    getMe()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  async function logout() {
    await apiLogout()
    queryClient.clear()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
